import '../icon/ui-icon.js';
import { Labels } from '../../global/labels.js';
import { UIElement } from '../ui-element.js';
import { scrollTo } from '../../global/helpers.js';
import {
    createElement,
    closestByCond,
    html,
    element,
    setAttributes,
    getCSSPropertyValue,
    extractSpecificElement,
} from '../../global/ui-helpers.js';
import { Digest } from '../../global/digest.js';
import { browser } from '../../global/browser.js';
import styles from './ui-curtain.css';

/**
 * @memberof SharedComponents
 * @implements {Activatable}
 * @augments {UIElement}
 * @classdesc Represents a class for <code>ui-curtain</code> element.
 * Curtain is opening section with content. Layout 'spoiler'
 * can be without container ui-curtains.
 * @fires event:curtain-toggle
 * @fires event:curtain-open
 * @fires event:curtain-close
 * @property {UICurtainLayout} layout {@attr layout} Type of layout.
 *  {@desc default}
 *  {@desc spoiler}
 * @property {boolean} [limited=false] {@attr limited} Deprecated: If true, curtain will not
 * be active. This property will be removed in next releases.
 * @property {boolean} [mobile] {@attr mobile} Curtain is shown only in smaller screen view.
 * @property {boolean} [autoscroll=false] {@attr autoscroll} If true: user scrolled to the current curtain after opening.
 * @property {boolean} [hideHead] {@attr hide-head} If true, curtain's head is hidden.
 * @property {string} [glyph] {@attr glyph} Glyph for icon on the left side (not used in spoiler)
 * @property {string} [label] {@attr label} Label of curtain.
 * @property {string} [labelOpen] {@attr label-open} Label of open button,
 * used in layout="spoiler".
 * @property {string} [labelClose] {@attr label-close} Label of close button,
 * used in layout="spoiler".
 * @property {boolean} [open=false] {@attr open} If true, curtain will be opened,
 * otherwise closed.
 * @property {number} [index] {@attr index} Index of numbered curtain (not used in spoiler)
 * @property {boolean} [completed=false] {@attr completed} If true, numbered curtain receive completed layout (not used in spoiler)
 * @property {boolean} [disabled] {@attr disabled} Special state for spoiler: if true - disabled (not used in curtain)
 * @property {HTMLDetailsElement} details {@readonly} Shortcut to curtain's main element.
 * @property {HTMLElement} head {@readonly} Shortcut to curtain's of head element.
 * @property {UIIcon} headIcon {@readonly} Shortcut to curtain's icon on the left of head-content.
 * @property {HTMLSpanElement} headTitle {@readonly} Shortcut to curtain's title in head-content.
 * @property {HTMLDivElement} headContent {@readonly} Shortcut to curtain's head-content element.
 * @property {HTMLDivElement} body {@readonly} Shortcut to curtain's body element.
 * @property {HTMLDivElement} content {@readonly} Shortcut to curtain's body's content element.
 * @alias UICurtain
 * @element ui-curtain
 * @slot
 * @example
 * <ui-curtain label="Open me">
 *   <p>Very intersting content</p>
 * </ui-curtain>
 */
class UICurtain extends UIElement {
    constructor() {
        super();
        /**
         * @type {Animation | null}
         * @private
         */
        this.animation = null;
        /**
         * @type {boolean}
         * @private
         */
        this.isClosing = false;
        /**
         * @type {boolean}
         * @private
         */
        this.isExpanding = false;
    }

    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels() {
        return Labels.attach('ui-curtain', {
            showLess: 'Show less',
            showMore: 'Show more',
        });
    }

    /**
     * Layout type of curtains.
     * @type {UICurtainLayouts}
     */
    static get Layout() {
        return {
            Default: 'default',
            Spoiler: 'spoiler',
        };
    }

    /**
     * @type {Array<string>}
     */
    static get clickableActions() {
        return ['a', 'ui-link', 'button', 'ui-button'];
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes() {
        return [
            'layout',
            'label',
            'label-open',
            'label-close',
            'open',
            'glyph',
            'disabled',
            'limited',
            'hide-head',
            'mobile',
            'index',
        ];
    }

    /**
     * @type {Props}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                layout: String,
                glyph: String,
                mobile: Boolean,
                autoscroll: Boolean,
                label: String,
                labelOpen: String,
                labelClose: String,
                index: Number,
                limited: {
                    type: Boolean,
                    validate: /** @type {AttributeValidationHandler} */ (
                        component
                    ) => {
                        console.warn(
                            `limited - attribute is deprecated.`,
                            component
                        );
                    },
                },
                completed: Boolean,
                disabled: Boolean,
                hideHead: Boolean,
                open: Boolean,
            },
            children: {
                details: 'details',
                head: '.ui-curtain__head',
                headIcon: '.ui-curtain__head-icon',
                headTitle: '.ui-curtain__head-title',
                headContent: '.ui-curtain__head-content',
                body: '.ui-curtain__body',
                content: '.ui-curtain__body-content',
            },
        };
    }

    /**
     * @returns {HTMLElement}
     */
    get toggle() {
        return this.head;
    }

    /**
     * Disable animation for '(prefers-reduced-motion: reduce)'
     * @returns {boolean}
     */
    isAnimated() {
        return !browser.prefersReducedMotion();
    }

    /**
     * Checks if it's a spoiler
     * @returns {boolean}
     */
    isSpoiler() {
        return this.layout === UICurtain.Layout.Spoiler;
    }

    /**
     * Checks if it's a spoiler
     * @returns {booleans}
     */
    isDisabledSpoiler() {
        return this.isSpoiler() && this.disabled;
    }

    /**
     * Checks if the head should be hidden
     * @returns {booleans}
     */
    isHeadHidden() {
        return (!browser.mobileMediaMatches() && this.mobile) || this.hideHead;
    }

    /**
     * Get text for spoiler title (opened/closed).
     * @returns {string}
     */
    getTitle() {
        if (this.label) {
            return this.label;
        }
        return this.open
            ? this.labelClose || UICurtain.labels.showLess
            : this.labelOpen || UICurtain.labels.showMore;
    }

    /**
     * Sets needed attributes and tags for numbered curtain
     */
    setAriaNumberedList() {
        this.details.setAttribute('role', 'list');
        setAttributes(this.head, {
            'role': 'listitem',
            'aria-roledescription': 'button',
            'aria-posinset': this.getAttribute('aria-posinset') || 1,
            'aria-setsize': this.getAttribute('aria-setsize') || 1,
        });
    }

    /**
     * Build chevron accordingly to the curtain status
     * @returns {HTMLElement}
     */
    buildChevronElement() {
        return element`
            <ui-icon
                class="ui-curtain__head-chevron"
                glyph="redesign-chevron-down"
            ></ui-icon>
        `;
    }

    /**
     * Build icon accordingly to the curtain attribute
     * @returns {HTMLElement}
     */
    buildIconElement() {
        return element`<ui-icon class='ui-curtain__head-icon' glyph="${this.glyph}"></ui-icon>`;
    }

    /**
     * Render curtain body with content.
     * @param {Array<ElementConfig | HTMLElement | Node | string>} contentNodes
     * @returns {Element}
     */
    renderBody(contentNodes) {
        const content = createElement({
            tagName: 'div',
            classList: {
                'ui-curtain__body-content': true,
            },
            children: contentNodes,
        });
        const bodyId = `ui-curtain-title-${this._uuid}`;
        const body = html`
            <div
                role="region"
                aria-labelledby="${bodyId}"
                aria-hidden="${!this.open}"
                class="ui-curtain__body"
            >
                ${content}
            </div>
        `;

        return body;
    }

    /**
     * Renders head of the curtain.
     * @param { HTMLCollection } children header children
     */
    renderHead(children) {
        let iconElement = this.glyph && this.buildIconElement();
        let amountElement;
        let childrenArray = children && Array.from(children);
        if (childrenArray?.length) {
            iconElement =
                iconElement || extractSpecificElement(childrenArray, 'ui-icon');
            amountElement = extractSpecificElement(childrenArray, 'ui-amount');
        }
        if (iconElement) {
            iconElement.classList.add('ui-curtain__head-icon');
            this.glyph = iconElement.glyph;
        }

        const isSpoiler = this.isSpoiler();
        const headId = `ui-curtain-title-${this._uuid}`;
        return element`
            <summary
                class="ui-curtain__head"
                role="button"
                aria-expanded="${this.open}"
                aria-disabled="${this.limited || this.isDisabledSpoiler()}"
                aria-hidden="${this.isHeadHidden()}"
            >
                ${!isSpoiler && iconElement}

                <div class="ui-curtain__head-content">
                    ${isSpoiler && this.buildChevronElement()}
                    <span
                        id="${headId}"
                        class="ui-curtain__head-title"
                    >${this.getTitle()}</span>
                    ${
                        !isSpoiler &&
                        !!childrenArray?.length &&
                        html`
                            <div class="ui-curtain__head-actions">
                                ${childrenArray.map((child) => html`${child}`)}
                            </div>
                        `
                    }
                </div>

                ${!isSpoiler && amountElement}
                ${!isSpoiler && this.buildChevronElement()}
            </summary>
        `;
    }

    /**
     * Changes current view to active (open) state.
     * @returns {UICurtain}
     */
    activate() {
        this.head.focus();
        /* istanbul ignore if */
        if (this.open) {
            return this;
        }

        /* istanbul ignore if */
        if (this.isAnimated()) {
            this.body.style.overflow = `hidden`;
            this.body.style.height = `0px`;
        }

        this.open = true;
        return this;
    }

    /**
     * Sliding up animation
     */
    /* istanbul ignore next */
    slideUp() {
        this.isClosing = true;

        this.body.style.height = `auto`;
        this.body.style.visibility = `visible`;

        const startHeight = `${this.body.offsetHeight}px`;
        const endHeight = `0px`;

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.body.animate(
            {
                height: [startHeight, endHeight],
            },
            {
                duration: getCSSPropertyValue('--ui-transition-time-slide'),
                easing: getCSSPropertyValue('--ui-transition-function'),
            }
        );

        this.animation.onfinish = () => {
            this.onAnimationFinish(false);
            this.details.open = false;
            this.headTitle.innerText = this.getTitle();
        };
        this.animation.oncancel = () => (this.isClosing = false);
    }

    /**
     * Sliding down animation
     */
    /* istanbul ignore next */
    slideDown() {
        this.isExpanding = true;

        const startHeight = `0px`;
        const endHeight = `${this.content.offsetHeight}px`;

        this.animation?.cancel();

        this.animation = this.body.animate(
            {
                height: [startHeight, endHeight],
            },
            {
                duration: getCSSPropertyValue('--ui-transition-time-slide'),
                easing: getCSSPropertyValue('--ui-transition-function'),
            }
        );

        this.animation.onfinish = () => {
            this.onAnimationFinish(true);
            this.headTitle.innerText = this.getTitle();
        };
        this.animation.oncancel = () => (this.isExpanding = false);
    }

    /**
     * Handling finishing of animation
     * @param {boolean} shouldOpen
     */
    onAnimationFinish(shouldOpen) {
        this.dispatchCustomEvent(shouldOpen ? 'curtain-open' : 'curtain-close');
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.body.removeAttribute('style');
        if (this.autoscroll && shouldOpen) {
            this.handleAutoScroll();
        }
    }

    /**
     * Handles curtains scroll on mobile.
     * @private
     */
    async handleAutoScroll() {
        const hasVerticalScroll =
            document.body.scrollHeight > document.body.clientHeight;
        if (browser.mobileMediaMatches() && hasVerticalScroll && this.open) {
            await scrollTo(this, 0);
        }
    }

    /**
     * Handles toggling with animation
     * @private
     * @param {boolean} shouldOpen
     */
    handleAnimatedToggle(shouldOpen) {
        this.body.style.overflow = 'hidden';
        if (this.isClosing || shouldOpen) {
            this.body.style.height = `${this.body.offsetHeight}px`;
            this.details.open = true;
            window.requestAnimationFrame(() => this.slideDown());
        } else if (this.isExpanding || !shouldOpen) {
            this.slideUp();
        }
    }

    /**
     * Handles toggling without animation
     * @private
     * @param {boolean} shouldOpen
     */
    handleStaticToggle(shouldOpen) {
        this.details.open = shouldOpen;
        this.headTitle.innerText = this.getTitle();
        if (this.autoscroll && this.open) {
            this.handleAutoScroll();
        }
        this.dispatchCustomEvent(this.open ? 'curtain-open' : 'curtain-close');
    }

    /**
     * Handles summary (head) click.
     * @private
     * @param {Event} event
     */
    handleClick(event) {
        // Prevent default details toggling
        event?.preventDefault();

        // Do not toggle after clicked on link or button inside summary (head)
        const clickableTarget = closestByCond(
            event.target,
            (node) => {
                return (
                    node.localName &&
                    UICurtain.clickableActions.includes(node.localName)
                );
            },
            (node) => {
                return !this.head.contains(node);
            }
        );
        if (clickableTarget) {
            return;
        }

        // Do not toggle after clicked on limited or disabled curtain/spoiler
        if (this.limited || this.isDisabledSpoiler()) {
            return;
        }

        // Otherwise - change the state open/close
        this.open = !this.open;

        // Legacy way: dispatch 'curtain-toggle' only after click
        this.dispatchCustomEvent('curtain-toggle', null);
    }

    /**
     * Handle default <details> toggle
     * @private
     */
    handleToggle(e) {
        // Details component has feature (in Chrome), that it opens automatically,
        // if info was found by page-in search in browser inside content.
        // This should also trigger component update.

        if (this.details.open === this.hasAttribute('open')) {
            return;
        }

        // Should follow limited/disabled logic
        if (this.limited || this.isDisabledSpoiler()) {
            if (this.details.open) {
                this.details.open = false;
            }
            return;
        }

        if (this.details.open) {
            this.setAttribute('open', '');
        } else {
            this.removeAttribute('open');
        }
        this.dispatchCustomEvent('curtain-toggle', null);
    }

    /**
     * Handles window resize.
     * @private
     */
    handleResize(e) {
        this.open = !browser.mobileMediaMatches();
        this.head.setAttribute('aria-hidden', !browser.mobileMediaMatches());
    }

    /**
     * Util for connecting event handlers.
     * @private
     */
    addEventListeners() {
        this.head.addEventListener('click', this.clickHandler);
        this.details.addEventListener('toggle', this.toggleHandler);
        /* istanbul ignore if */
        if (this.hasAttribute('mobile')) {
            browser.mobileMedia.addEventListener('change', this.resizeHandler);
        }
    }

    /**
     * Util for disconnecting event handlers.
     * @private
     */
    removeEventListeners() {
        this.head.removeEventListener('click', this.clickHandler);
        this.details.removeEventListener('toggle', this.toggleHandler);
        /* istanbul ignore if */
        if (this.hasAttribute('mobile')) {
            browser.mobileMedia.removeEventListener(
                'change',
                this.resizeHandler
            );
        }
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name, oldValue, newValue) {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }

        // Variable to format boolean attribute to the proper true/false values
        const newBoolean = newValue !== null;

        switch (name) {
            case 'label':
            case 'label-open':
            case 'label-close':
                this.headTitle.innerText = this.getTitle();
                break;
            case 'glyph':
                if (!newValue && this.headIcon) {
                    this.head.removeChild(this.headIcon);
                    break;
                }

                if (this.headIcon) {
                    this.headIcon.setAttribute('glyph', newValue);
                } else {
                    this.head.insertBefore(
                        this.buildIconElement(),
                        this.headContent
                    );
                }
                break;
            case 'open':
                // If animation is needed, use animated toggling
                if (this.isAnimated()) {
                    /* istanbul ignore next */
                    this.handleAnimatedToggle(newBoolean);
                } else {
                    this.handleStaticToggle(newBoolean);
                }
                this.head.setAttribute('aria-expanded', newBoolean);
                this.body.setAttribute('aria-hidden', !newBoolean);
                break;
            case 'disabled':
            case 'limited':
                if (name === 'disabled' && !this.isSpoiler()) {
                    break;
                }
                this.head.setAttribute('aria-disabled', newBoolean);
                break;
            case 'hide-head':
                this.head.setAttribute('aria-hidden', newBoolean);
            case 'mobile':
                this.head.setAttribute(
                    'aria-hidden',
                    newBoolean && !browser.mobileMediaMatches()
                );
                break;
            case 'index':
                if (newBoolean) {
                    this.setAriaNumberedList();
                } else {
                    this.details.removeAttribute('role');
                    setAttributes(this.head, {
                        'role': 'button',
                        'aria-roledescription': null,
                        'aria-posinset': null,
                        'aria-setsize': null,
                    });
                }
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render() {
        this._uuid = Digest.randomId();
        const childNodes = this.detachChildNodes();
        const header = childNodes
            .filter((node) => {
                return node.tagName && node.tagName.toLowerCase() === 'header';
            })
            .shift();
        const section = childNodes
            .filter((node) => {
                return node.tagName && node.tagName.toLowerCase() === 'section';
            })
            .shift();

        const contentNodes = section
            ? section.childNodes
            : [].filter.call(
                  childNodes,
                  (node) => node?.localName !== 'header'
              );

        if (this.isHeadHidden()) {
            this.open = true;
        }

        this.append(html`
            <details open=${this.open || null}>
                ${this.renderHead(header?.children)}
                ${this.renderBody(contentNodes)}
            </details>
        `);

        if (this.hasAttribute('index') && !this.isSpoiler()) {
            this.setAriaNumberedList();
        }
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.clickHandler = this.handleClick.bind(this);
        this.toggleHandler = this.handleToggle.bind(this);
        this.resizeHandler = this.handleResize.bind(this);
        this.addEventListeners();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.removeEventListeners();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.addEventListeners();
    }
}

UICurtain.defineElement('ui-curtain', styles);
export { UICurtain };
